import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

type Status = 'available' | 'busy' | 'dnd';
const DOT: Record<Status, string> = { available: 'var(--green)', busy: 'var(--orange)', dnd: 'var(--red)' };

/**
 * Selector de MI presencia en el topbar (feedback 2026-07-30: "poder ponernos no available
 * desde el app y el phone"). Escribe en RingCentral (permiso EditPresence) → el cambio también
 * se ve en el softphone/teléfono. Si el usuario no está ligado a una extensión, no se muestra.
 */
export function MyPresencePicker() {
  const { t } = useI18n();
  const [linked, setLinked] = useState(false);
  const [status, setStatus] = useState<Status>('available');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get<{ linked: boolean; status: Status | null }>('/api/presence/me')
      .then((r) => { setLinked(r.linked); if (r.status) setStatus(r.status); })
      .catch(() => { /* best-effort */ });
  }, []);

  // Cerrar al hacer click afuera.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!linked) return null;

  async function pick(s: Status) {
    setBusy(true); setErr(null);
    try {
      await api.put('/api/presence/me', { status: s });
      setStatus(s);
      setOpen(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Error');
    } finally { setBusy(false); }
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn ghost sm"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        title={t('pres.myStatus')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 9px' }}
      >
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: DOT[status], flexShrink: 0 }} />
        <span style={{ fontSize: 'var(--fs-xs)' }}>{t(`pres.${status}`)}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 300, minWidth: 190,
          background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)', padding: 4,
        }}>
          {(['available', 'busy', 'dnd'] as Status[]).map((s) => (
            <button key={s} className="btn ghost sm" disabled={busy} onClick={() => pick(s)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'flex-start', padding: '7px 10px' }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: DOT[s], flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--fs-sm)' }}>{t(`pres.${s}`)}</span>
              {status === s && <span style={{ marginLeft: 'auto', color: 'var(--green)', display: 'inline-flex' }}><Check size={13} strokeWidth={1.75} /></span>}
            </button>
          ))}
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: '4px 10px 6px' }}>{t('pres.syncsToPhone')}</div>
          {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)', padding: '0 10px 6px' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}
